import * as ZXing from "@zxing/library";
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Webcam from "react-webcam";
import { Error, ErrorType } from "../passapp-sdk/models/Error";
import { configSetIsScanning } from "../redux/actions/configActions";
import { ISearchUser } from "../store/types/userTypes";
import { isSeparatorCharacters, scannerSeparatorsCharacters } from "../utils/scannerSeparators";
import useScanMRZ from "./useScanMRZ";
import useScanPDF417 from "./useScanPDF417";
import useScanDocumentQr from "./useScanDocumentQr";
import { isJwtShaped } from "../utils/scanDocumentQrParser";
import User from "../passapp-sdk/user";
import Pass from "../passapp-sdk/pass";
import { useStore } from "./useStore";
import { useTranslation } from "./useTranslation";
import { isMac } from "../utils/platform";

// Characters a Windows-configured HID scanner may emit in place of "@" when running on Mac.
// U+00BA = masculine ordinal (º), U+00AA = feminine ordinal (ª), U+0040 = @ (already valid).
const MAC_AT_ALTERNATES: string[] = ["º", "ª"];

const normalizeMacSeparator = (value: string): string => {
  if (isSeparatorCharacters(value)) return value;
  let normalized = value;
  for (const alt of MAC_AT_ALTERNATES) {
    if (normalized.includes(alt)) {
      normalized = normalized.split(alt).join("@");
    }
  }
  return normalized;
};

const keyboardLayouts: any = {
  english: {
    81: "Q",
    87: "W",
    69: "E",
    82: "R",
    84: "T",
    89: "Y",
    85: "U",
    73: "I",
    79: "O",
    80: "P",
    219: "[",
    221: "]",
    220: "\\",
    65: "A",
    83: "S",
    68: "D",
    70: "F",
    71: "G",
    72: "H",
    74: "J",
    75: "K",
    76: "L",
    186: ";",
    222: "'",
    90: "Z",
    88: "X",
    67: "C",
    86: "V",
    66: "B",
    78: "N",
    77: "M",
    188: ",",
    190: ".",
    191: "/",
    192: "`",
    49: "1",
    50: "2",
    51: "3",
    52: "4",
    53: "5",
    54: "6",
    55: "7",
    56: "8",
    57: "9",
    48: "0",
    189: "-",
    187: "=",
    112: "F1",
    113: "F2",
    114: "F3",
    115: "F4",
    116: "F5",
    117: "F6",
    118: "F7",
    119: "F8",
    120: "F9",
    121: "F10",
    122: "F11",
    123: "F12",
  },
  spanish: {
    81: "Q",
    87: "W",
    69: "E",
    82: "R",
    84: "T",
    89: "Y",
    85: "U",
    73: "I",
    79: "O",
    80: "P",
    209: "Ñ",
    180: "´",
    186: "¨",
    65: "A",
    83: "S",
    68: "D",
    70: "F",
    71: "G",
    72: "H",
    74: "J",
    75: "K",
    76: "L",
    221: "Ñ",
    192: "´",
    90: "Z",
    88: "X",
    67: "C",
    86: "V",
    66: "B",
    78: "N",
    77: "M",
    188: ",",
    190: ".",
    189: "-",
    32: " ",
    49: "1",
    50: "2",
    51: "3",
    52: "4",
    53: "5",
    54: "6",
    55: "7",
    56: "8",
    57: "9",
    48: "0",
    191: "¿",
    222: "¡",
    112: "F1",
    113: "F2",
    114: "F3",
    115: "F4",
    116: "F5",
    117: "F6",
    118: "F7",
    119: "F8",
    120: "F9",
    121: "F10",
    122: "F11",
    123: "F12",
  },
  portuguese: {
    81: "Q",
    87: "W",
    69: "E",
    82: "R",
    84: "T",
    89: "Y",
    85: "U",
    73: "I",
    79: "O",
    80: "P",
    219: "[",
    221: "]",
    220: "\\",
    65: "A",
    83: "S",
    68: "D",
    70: "F",
    71: "G",
    72: "H",
    74: "J",
    75: "K",
    76: "L",
    186: "Ç",
    222: "'",
    90: "Z",
    88: "X",
    67: "C",
    86: "V",
    66: "B",
    78: "N",
    77: "M",
    188: ",",
    190: ".",
    191: ";",
    192: "`",
    49: "1",
    50: "2",
    51: "3",
    52: "4",
    53: "5",
    54: "6",
    55: "7",
    56: "8",
    57: "9",
    48: "0",
    189: "-",
    187: "+",
    112: "F1",
    113: "F2",
    114: "F3",
    115: "F4",
    116: "F5",
    117: "F6",
    118: "F7",
    119: "F8",
    120: "F9",
    121: "F10",
    122: "F11",
    123: "F12",
  },
  french: {
    81: "A",
    87: "Z",
    69: "E",
    82: "R",
    84: "T",
    89: "Y",
    85: "U",
    73: "I",
    79: "O",
    80: "P",
    219: "^",
    221: "$",
    220: "*",
    65: "Q",
    83: "S",
    68: "D",
    70: "F",
    71: "G",
    72: "H",
    74: "J",
    75: "K",
    76: "L",
    186: "M",
    222: "%",
    90: "W",
    88: "X",
    67: "C",
    86: "V",
    66: "B",
    78: "N",
    77: ",",
    188: ";",
    190: ":",
    191: "!",
    192: "ù",
    49: "&",
    50: "é",
    51: '"',
    52: "'",
    53: "(",
    54: "-",
    55: "è",
    56: "_",
    57: "ç",
    48: "à",
    189: ")",
    187: "=",
    112: "F1",
    113: "F2",
    114: "F3",
    115: "F4",
    116: "F5",
    117: "F6",
    118: "F7",
    119: "F8",
    120: "F9",
    121: "F10",
    122: "F11",
    123: "F12",
  },
};

const useScan = (video?: React.RefObject<Webcam> | null) => {
  const { scanPDF, resultPDF, resetPDF } = useScanPDF417();
  const { scanDocumentQr, resultDocumentQr, resetDocumentQr } = useScanDocumentQr();
  const { data, isMrz, recognize } = useScanMRZ(video);
  const [codeReaderVideo] = useState(new ZXing.BrowserMultiFormatReader());
  const [scanning, setScanning] = useState(false);
  let codeScanned: string = "";
  const [resultQrPdfScanner, setResultQrPdfScanner] = useState("");
  const [enableMrz, setEnableMrz] = useState(false);
  const [result, setResult] = useState<ISearchUser | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const globalDispatch = useDispatch();

  const { authStore } = useStore();
  const { t, BundleKey } = useTranslation();
  const [userSdk] = useState(new User());
  const [passSdk] = useState(new Pass());

  const isMyQR = (val: string): boolean => {
    return val.length <= 87;
  };

  const scanQR = useCallback(
    (val: string) => {
      if (isMyQR(val)) {
        userSdk
          .findUserByLPQR(authStore.token, val)
          .then((response: any) => {
            setResult({
              qr: response.data.id,
              rawQr: val,
              userData: response.data,
            });
          })
          .catch((err: any) => {
            const errorMsg = t(BundleKey.DIALOG_QREXPIRE_MESSAGE) || err;
            setError({
              type: ErrorType.EXPIRE,
              title: t(BundleKey.DIALOG_QREXPIRE_TITLE),
              message: errorMsg,
            });
          });
      } else {
        passSdk
          .findPassByLPQR(authStore.token, val)
          .then((response: any) => {
            const guest = response.data?.guest || response.data?.guests?.[0] || response.data;
            const passId = response.data?.id || guest?.id;
            const passUserId = guest?.id;
            setResult({
              pass: passId,
              passUserId,
              rawPass: val,
              userData: response.data,
            });
          })
          .catch((err: any) => {
            const errorMsg = t(BundleKey.DIALOG_QREXPIRE_MESSAGE) || err;
            setError({
              type: ErrorType.EXPIRE,
              title: t(BundleKey.DIALOG_QREXPIRE_TITLE),
              message: errorMsg,
            });
          });
      }
    },
    [userSdk, authStore.token, passSdk, t, BundleKey]
  );

  const isPDF = (value: string): boolean => {
    if (value) {
      return isSeparatorCharacters(value);
    }
    return false;
  };

  const scanQrPdfCamera = (videoRef: React.RefObject<Webcam> | null, device: string) => {
    resetScanQrPdfCamera();
    codeReaderVideo.timeBetweenDecodingAttempts = 400;
    if (videoRef?.current) {
      setScanning(true);
      codeReaderVideo.decodeFromVideoDevice(device, videoRef.current.video, (result, err) => {
        if (result) {
          // stopAsyncDecode/stopContinuousDecode sólo paran el loop de lectura: el stream de
          // cámara sigue prendido hasta el reset() completo, que hoy sólo corre al desmontar
          // el componente Scan. Si la navegación post-scan se traba (ver EntryEvent.tsx), la
          // cámara queda decodificando video de fondo indefinidamente.
          resetScanQrPdfCamera();
          const text = result.getText();
          if (isJwtShaped(text)) {
            scanDocumentQr(text);
          } else if (isPDF(text)) {
            scanPDF(text);
          } else {
            scanQR(text);
          }
        }
        if (err && !(err instanceof ZXing.NotFoundException)) {
          console.error(err);
        }
      });
    }
  };

  const resetScanQrPdfCamera = () => {
    codeReaderVideo.stopAsyncDecode();
    codeReaderVideo.stopContinuousDecode();
    codeReaderVideo.reset();
    setScanning(false);
  };

  const stopScanQrPdfCamera = () => {
    codeReaderVideo.stopAsyncDecode();
    codeReaderVideo.stopContinuousDecode();
    setScanning(false);
  };

  // Umbral de longitud para distinguir scanner vs entrada manual.
  // Todo código de scanner (QR/PDF417) procesado por esta app tiene >30 chars;
  // todo DNI/pasaporte manual tiene <15 chars. Margen seguro: 30.
  const MIN_SCAN_CODE_CHARS = 30;

  const scanQrPdfScanner = (e: any) => {
    // Autorepeat del OS nunca lo emite un scanner HID.
    if (e.repeat) return;
    const code = e.code;
    const key = e.key;
    if (code === "Backspace") return;

    const isTerminator =
      code === "Enter" ||
      code === "NumpadEnter" ||
      code === "Tab" ||
      key === "Enter" ||
      key === "Tab";

    // ── Decisión al final ────────────────────────────────────────────────────
    // No interceptamos chars individuales — dejamos que el input los reciba
    // mientras el buffer también los acumula en paralelo. Al llegar el Enter:
    //
    //   • Si codeScanned ≥ 30 chars → ES SCANNER. Bloqueamos el Enter con
    //     stopImmediatePropagation (con el listener en capture phase, esto
    //     evita que el onKeyDown sintético de React dispare handleManualSearch
    //     del input). Disparamos onScan vía getStringFromScanner.
    //
    //   • Si codeScanned < 30 chars → ES MANUAL. Dejamos pasar el Enter al
    //     input para que handleManualSearch corra normalmente. NO disparamos onScan.
    //
    // Velocidad del tipeo es irrelevante: la decisión se basa en el contenido
    // final del buffer al momento del Enter, no en timing.
    if (isTerminator && codeScanned.length >= MIN_SCAN_CODE_CHARS) {
      e.preventDefault?.();
      e.stopImmediatePropagation?.();
    }

    getStringFromScanner(code, key, MIN_SCAN_CODE_CHARS);
  };

  const translateToEnglish = (code: string, fromLanguage: string) => {
    const keyCode = code.toUpperCase().charCodeAt(0);
    if (keyboardLayouts[fromLanguage] && keyboardLayouts[fromLanguage][keyCode]) {
      const foreignKey = keyboardLayouts[fromLanguage][keyCode];
      return Object.keys(keyboardLayouts.english).find(
        (englishKeyCode) => keyboardLayouts.english[englishKeyCode] === foreignKey
      );
    }
    return null;
  };

  const isValidSeparator = (char: string) => {
    return scannerSeparatorsCharacters.includes(char);
  };

  const inferInputLanguage = (key: string): string | null => {
    for (const lang of ["spanish", "portuguese", "french"]) {
      if (keyboardLayouts[lang][key.toUpperCase().charCodeAt(0)]) {
        const translatedKey = translateToEnglish(key, lang);
        if (translatedKey && isValidSeparator(translatedKey)) {
          return lang;
        }
      }
    }
    return null;
  };

  const getStringFromScanner = (code: string, key: string, minScanChars: number = 30) => {
    if (code === undefined || key === undefined) return;
    // Treat Enter / NumpadEnter / Tab as the scan terminator regardless of which
    // field `code` vs `key` carries it — different HID scanners emit different combos.
    if (
      code === "Enter" ||
      code === "NumpadEnter" ||
      code === "Tab" ||
      code === "" ||
      key === "Enter" ||
      key === "Tab"
    ) {
      // Solo dispara onScan si el buffer es del tamaño de un código de scanner.
      // Si es más corto, el Enter es del usuario submitting un DNI manual —
      // no queremos procesarlo como scan (el endpoint fallaría).
      if (codeScanned.length >= minScanChars) {
        setResultQrPdfScanner(codeScanned);
      }
      // Reset buffer immediately after terminator so the next scan starts clean.
      codeScanned = "";
      return;
    }
    switch (code) {
      case "ControlLeft":
      case "AltRight":
      case "ShiftLeft":
      case "ShiftRight":
      case "Control":
      case "CapsLock":
        break;
      default:
        if (isMac()) {
          if (key.length === 1) {
            codeScanned += key;
          }
        } else if (!keyboardLayouts.english[code.toUpperCase().charCodeAt(0)]) {
          const inputLanguage = inferInputLanguage(key);
          if (inputLanguage) {
            const englishKey = translateToEnglish(key, inputLanguage);
            if (englishKey) {
              codeScanned += englishKey;
            }
          }
        } else {
          codeScanned += key;
        }
    }
  };

  const reset = () => {
    setError(null);
    setResult(null);
    resetPDF();
    resetDocumentQr();
    setResultQrPdfScanner("");
  };

  useEffect(() => {
    if (data != null && enableMrz) {
      setEnableMrz(false);
    }
  }, [data, enableMrz]);

  useEffect(() => {
    if (resultQrPdfScanner !== "") {
      const scannedValue = resultQrPdfScanner;
      // Se limpia ya: `scanPDF`/`scanDocumentQr` no están memoizadas (cambian de referencia en
      // cada render), así que sin este reset cualquier re-render posterior — incluso el que
      // dispara el propio resultado del escaneo, falle o no — vuelve a entrar acá con el mismo
      // valor y dispara `scanQR`/`previewInvite`/`getUser` de nuevo: loop infinito que funde la
      // RAM y bombardea la API.
      setResultQrPdfScanner("");
      setTimeout(() => {
        const normalized = isMac() ? normalizeMacSeparator(scannedValue) : scannedValue;
        if (isJwtShaped(normalized)) {
          scanDocumentQr(normalized);
        } else if (isPDF(normalized)) {
          scanPDF(normalized);
        } else {
          scanQR(normalized);
        }
        globalDispatch(configSetIsScanning(false));
      }, 0);
    }
  }, [globalDispatch, resultQrPdfScanner, scanDocumentQr, scanPDF, scanQR]);

  useEffect(() => {
    if (resultPDF) {
      setResult({
        dni: resultPDF.dni,
        countryCode: resultPDF.country,
        firstname: resultPDF.firstname,
        lastname: resultPDF.lastname,
        sex: resultPDF.sex,
        birthdate: resultPDF.birthdate,
      });
    }
  }, [resultPDF]);

  useEffect(() => {
    if (resultDocumentQr) {
      setResult({
        dni: resultDocumentQr.dni,
        countryCode: resultDocumentQr.country,
        firstname: resultDocumentQr.firstname,
        lastname: resultDocumentQr.lastname,
        sex: resultDocumentQr.sex,
        birthdate: resultDocumentQr.birthdate,
      });
    }
  }, [resultDocumentQr]);

  useEffect(() => {
    if (data) {
      setResult({
        dni: data.dni,
        countryCode: data.country,
        firstname: data.firstname,
        lastname: data.lastname,
      });
    }
  }, [data]);

  return {
    scanPDF,
    scanQR,
    resetScanQrPdfCamera,
    stopScanQrPdfCamera,
    scanQrPdfCamera,
    scanQrPdfScanner,
    recognize,
    isMrz,
    enableMrz,
    setEnableMrz,
    scanning,
    result,
    error,
    reset,
  };
};

export default useScan;
